import * as XLSX from 'xlsx';
import {
    OrderQuotaCustomerTier,
    OrderQuotaDetail,
    OrderQuotaImportRowInput,
    OrderQuotaTierImportRowInput,
} from '../graphql/orderQuotaGraphql';
import { overrideOf } from './orderQuotaGrid';

/** 模板与导入文件的固定列：编码、名称、日配额，其后为日期列 */
const FIXED_COLUMNS = 3;

/** 单次导入的最多数据行，与后端一致 */
export const MAX_IMPORT_ROWS = 2_000;

/** 提示里最多列出的错误行号个数，其余以「等」概括 */
export const MAX_LISTED_LINE_NUMBERS = 10;

/** 导入文件前三列的表头：编码、名称、每天默认（客户分级文件为客户ID、名称、分级） */
export type FixedHeaders = [string, string, string];

/** 模板可能在任一界面语言下下载，前三列表头按这几种语言都接受 */
export const TEMPLATE_LANGUAGES = ['zh', 'en', 'es'];

/** 前三列表头与模板不一致时抛出的错误信息，弹窗据此提示使用下载的模板 */
export const TEMPLATE_HEADER_MISMATCH = 'TEMPLATE_HEADER_MISMATCH';

/** 按各界面语言生成可接受的前三列表头 */
export const fixedHeaderOptions = (keys: FixedHeaders, translate: (language: string, key: string) => string): FixedHeaders[] =>
    TEMPLATE_LANGUAGES.map((language) => keys.map((key) => translate(language, key)) as FixedHeaders);

/** 某段模板文字在各界面语言下的取值，上传时都接受 */
export const templateTextOptions = (key: string, translate: (language: string, key: string) => string): string[] =>
    TEMPLATE_LANGUAGES.map((language) => translate(language, key));

const headerKey = (text: string): string => text.trim().toLowerCase();

/**
 * 导入按列的位置读取，增删或调换前三列会把值读错列（如删掉名称列后，第一个日期列被当成每天默认值），
 * 所以前三列表头须与某种语言的模板一致，否则整份拒绝。
 */
const assertFixedHeaders = (headerRow: string[], options: FixedHeaders[]): void => {
    const actual = headerRow.slice(0, 3).map(headerKey);
    const matched = options.some((expected) => expected.every((text, index) => headerKey(text) === actual[index]));
    if (!matched) throw new Error(TEMPLATE_HEADER_MISMATCH);
};

/** 把错误行号格式化为「3、4、9」，超过上限时末尾加「等」 */
export const formatLineNumbers = (lineNos: number[], max: number = MAX_LISTED_LINE_NUMBERS): string => {
    const sorted = [...lineNos].sort((a, b) => a - b);
    const shown = sorted.slice(0, max).join('、');
    return sorted.length > max ? `${shown} 等` : shown;
};

export interface TemplateHeaders {
    code: string;
    name: string;
    dailyQuota: string;
}

/**
 * 生成导入模板（含当前配置）：每行一个管控对象，日期列只含今日及以后的日期，已有按日配额的格子预填。
 */
export const buildTemplateWorkbook = (
    detail: OrderQuotaDetail,
    dates: string[],
    headers: TemplateHeaders
): XLSX.WorkBook => {
    const futureDates = dates.filter((date) => date >= detail.today);
    const rows: (string | number)[][] = [[headers.code, headers.name, headers.dailyQuota, ...futureDates]];
    detail.rows.forEach((row) => {
        rows.push([
            row.targetCode,
            row.targetName,
            row.dailyQuota ?? '',
            ...futureDates.map((date) => overrideOf(row, date) ?? ''),
        ]);
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), 'capacity');
    return workbook;
};

/** 模板文件名：capacity-<HUB 名称 | all>-<美西今日>.xlsx */
export const templateFileName = (hubLabel: string | null | undefined, today: string): string =>
    `capacity-${hubLabel || 'all'}-${today}.xlsx`;

export interface TierTemplateHeaders {
    channelId: string;
    name: string;
    tier: string;
    /** 选填 HUB 列表头上的标注，如「（测试仓，可不填）」 */
    optionalHubMark: string;
}

/** 客户分级文件的一个 HUB 列 */
export interface TierHubColumn {
    /** HUB 编码（分拣码）：按它对应各客户的值 */
    hubCode: string;
    /** 表头显示的 HUB 名称；为空时用 HUB 编码 */
    hubName: string;
    /** 选填（测试 HUB）：表头带标注，可留空或删除该列 */
    optional: boolean;
}

/** 客户分级文件的固定列：客户ID、名称、分级，其后每列一个尾程 HUB（列名为 HUB 名称） */
const TIER_FIXED_COLUMNS = 3;

/**
 * 客户分级文件：客户ID、名称、分级（高优 / 低优 / 普通），其后每个尾程 HUB 一列每天默认值（高优为保障值、低优为上限）；
 * 当前分级名单与各 HUB 的值预填。选填 HUB 的表头为「HUB 名称 标注」。
 *
 * @param hubs 尾程 HUB 列，按页面展示顺序（测试 HUB 在最后）
 */
export const buildTierWorkbook = (
    tiers: OrderQuotaCustomerTier[],
    headers: TierTemplateHeaders,
    tierLabel: (tier: OrderQuotaCustomerTier['tier']) => string,
    hubs: TierHubColumn[]
): XLSX.WorkBook => {
    const hubHeaders = hubs.map((hub) => {
        const label = hub.hubName || hub.hubCode;
        return hub.optional ? `${label} ${headers.optionalHubMark}` : label;
    });
    const rows: (string | number)[][] = [[headers.channelId, headers.name, headers.tier, ...hubHeaders]];
    tiers.forEach((item) => {
        const valueByHub = new Map(item.values.map((value) => [value.hubCode, value.dailyQuota]));
        rows.push([item.channelId, item.channelName, tierLabel(item.tier), ...hubs.map((hub) => valueByHub.get(hub.hubCode) ?? '')]);
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), 'customer-tier');
    return workbook;
};

/** 客户分级文件名：customer-tier-<美西今日>.xlsx */
export const tierFileName = (today: string): string => `customer-tier-${today}.xlsx`;

export const downloadWorkbook = (workbook: XLSX.WorkBook, fileName: string): void => {
    const data = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const url = URL.createObjectURL(new Blob([data], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * 日期列表头统一为 yyyy-MM-dd：兼容 Excel 把表头识别成日期后显示的 M/D/YY、M/D/YYYY；
 * 其余格式原样交给后端校验（后端会提示无法识别的日期列）。
 */
export const normalizeDateHeader = (raw: string): string => {
    const text = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (slash) {
        const year = slash[3].length === 2 ? `20${slash[3]}` : slash[3];
        return `${year}-${pad(Number(slash[1]))}-${pad(Number(slash[2]))}`;
    }
    const dashed = text.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/);
    if (dashed) return `${dashed[1]}-${pad(Number(dashed[2]))}-${pad(Number(dashed[3]))}`;
    return text;
};

const cellText = (value: unknown): string => (value === null || value === undefined ? '' : String(value).trim());

/**
 * 读取第一个工作表为单元格文本表格；没有工作表时返回空。
 * CSV 按 UTF-8 解码（不带 BOM 时默认按单字节解码，中文表头会成乱码而被判为表头不一致）；xlsx / xls 不受影响。
 */
const readFirstSheet = (data: ArrayBuffer): string[][] => {
    const workbook = XLSX.read(data, { type: 'array', codepage: 65001 });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) return [];
    return XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' }).map((cells) => cells.map(cellText));
};

/** HUB 列表头去掉选填标注得到 HUB（名称或分拣码，由后端识别）；不带标注时原样返回 */
const tierHubHeader = (header: string, optionalHubMarks: string[]): string => {
    const mark = optionalHubMarks.find((item) => item !== '' && header.endsWith(item));
    return mark ? header.slice(0, header.length - mark.length).trim() : header;
};

/**
 * 解析客户分级文件：第 1 列客户ID、第 3 列分级（第 2 列名称仅供阅读），其后表头非空的列为各 HUB 的每天默认值；
 * 空单元格表示不修改，HUB 列名（HUB 名称或分拣码）原样提交，由后端识别与校验；空行跳过，lineNo 为 Excel 行号。
 *
 * @param fixedHeaders 可接受的前三列表头，不一致时抛出 [TEMPLATE_HEADER_MISMATCH]
 * @param optionalHubMarks 可接受的选填 HUB 标注（各界面语言），表头去掉标注后为 HUB 名称或分拣码
 */
export const parseTierSheet = (
    data: ArrayBuffer,
    fixedHeaders: FixedHeaders[],
    optionalHubMarks: string[]
): OrderQuotaTierImportRowInput[] => {
    const table = readFirstSheet(data);
    if (table.length === 0) return [];
    assertFixedHeaders(table[0], fixedHeaders);
    const hubColumns = table[0]
        .map((header, index) => ({ hubCode: tierHubHeader(header, optionalHubMarks), index }))
        .filter(({ hubCode, index }) => index >= TIER_FIXED_COLUMNS && hubCode !== '');
    const rows: OrderQuotaTierImportRowInput[] = [];
    table.slice(1).forEach((values, index) => {
        if (values.every((value) => value === '')) return;
        rows.push({
            lineNo: index + 2,
            channelId: values[0] || null,
            tier: values[2] || null,
            values: hubColumns.map(({ hubCode, index: column }) => ({ hubCode, dailyQuota: values[column] || null })),
        });
    });
    return rows;
};

/**
 * 解析导入文件的第一个工作表为后端导入入参；空行跳过，lineNo 为 Excel 中的行号（表头为第 1 行）。
 *
 * @param fixedHeaders 可接受的前三列表头，不一致时抛出 [TEMPLATE_HEADER_MISMATCH]
 */
export const parseQuotaSheet = (data: ArrayBuffer, fixedHeaders: FixedHeaders[]): OrderQuotaImportRowInput[] => {
    const table = readFirstSheet(data);
    if (table.length === 0) return [];
    assertFixedHeaders(table[0], fixedHeaders);
    const dateHeaders = table[0].slice(FIXED_COLUMNS).map((cell) => normalizeDateHeader(cell));
    const rows: OrderQuotaImportRowInput[] = [];
    table.slice(1).forEach((values, index) => {
        if (values.every((value) => value === '')) return;
        rows.push({
            lineNo: index + 2,
            targetCode: values[0] || null,
            dailyQuota: values[2] || null,
            overrides: dateHeaders
                .map((date, i) => ({ date, value: values[FIXED_COLUMNS + i] || null }))
                .filter((cell) => cell.date !== '' && cell.value !== null),
        });
    });
    return rows;
};
